/**
 * ULD批量删除Modal组件
 * 
 * 功能：
 * - 批量删除选中的ULD
 * - 支持删除原因输入
 * - 显示要删除的ULD列表和详细信息
 * - 提供删除结果反馈
 */

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Alert,
    Space,
    Typography,
    Divider,
    List,
    Card,
    message,
    Tag
} from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { uldService } from '../../../../api/enhancedApi';
import { ULD_CORE_STATUS_NAMES, ULD_AVAILABILITY_NAMES } from '../constants/uldConstantsV3';

const { Text, Title } = Typography;
const { TextArea } = Input;

const BatchDeleteModal = ({ visible, onCancel, onSuccess, selectedUlds = [] }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    // 计算统计信息 - 使用V3模型字段
    const totalUlds = selectedUlds.length;
    const uldsByType = selectedUlds.reduce((acc, uld) => {
        const type = uld.master.uld_type;
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {});
    
    // 按核心状态分布统计（V3模型）
    const uldsByCoreStatus = selectedUlds.reduce((acc, uld) => {
        const coreStatus = uld.stock?.core_status || 'IN_STOCK';
        const statusName = ULD_CORE_STATUS_NAMES[coreStatus] || coreStatus;
        acc[statusName] = (acc[statusName] || 0) + 1;
        return acc;
    }, {});

    // 按可用性分布统计（V3模型）
    const uldsByAvailability = selectedUlds.reduce((acc, uld) => {
        const availability = uld.stock?.availability || 'AVAILABLE';
        const availabilityName = ULD_AVAILABILITY_NAMES[availability] || availability;
        acc[availabilityName] = (acc[availabilityName] || 0) + 1;
        return acc;
    }, {});

    useEffect(() => {
        if (visible) {
            // 重置表单
            form.setFieldsValue({
                delete_reason: ""
            });
        }
    }, [visible, form]);

    // 处理提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // 提取ULD识别码
            const uldIdentifiers = selectedUlds.map(uld => uld.master.uld_identifier);
            
            // 调用批量删除API
            const response = await uldService.batchDeleteUlds({
                uld_identifiers: uldIdentifiers,
                delete_reason: values.delete_reason
            });

            // 按照API_GUIDELINES规范处理响应格式
            const apiResult = response.data;
            const result = apiResult.data;

            // 显示操作结果
            if (result.success_count === selectedUlds.length) {
                // 全部成功
                Modal.success({
                    title: "删除成功",
                    content: `已成功删除 ${result.success_count} 个ULD`,
                });
                onSuccess?.();
            } else if (result.success_count > 0) {
                // 部分成功
                Modal.warning({
                    title: "部分删除成功",
                    content: (
                        <div>
                            <p>成功删除 {result.success_count} 个ULD</p>
                            <p>失败 {result.failure_count} 个ULD</p>
                            {result.failure_items && result.failure_items.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">失败原因：</Text>
                                <ul>
                                    {result.failure_items.map((item, index) => (
                                        <li key={index}>
                                            <Text code>{item.identifier}</Text>: {item.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            )}
                        </div>
                    ),
                });
                onSuccess?.();
            } else {
                // 全部失败
                Modal.error({
                    title: "删除失败",
                    content: (
                        <div>
                            <p>所有ULD删除失败</p>
                            {result.failure_items && result.failure_items.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text type="secondary">失败原因：</Text>
                                <ul>
                                    {result.failure_items.map((item, index) => (
                                        <li key={index}>
                                            <Text code>{item.identifier}</Text>: {item.error}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            )}
                        </div>
                    ),
                });
            }

        } catch (error) {
            console.error('批量删除ULD失败:', error);
            message.error(error.response?.data?.detail || '批量删除ULD失败');
        } finally {
            setLoading(false);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel?.();
    };

    // 获取ULD的状态显示信息（V3模型）
    const getUldStatusDisplay = (uld) => {
        const coreStatus = uld.stock?.core_status || 'IN_STOCK';
        const availability = uld.stock?.availability || 'AVAILABLE';
        const coreStatusName = ULD_CORE_STATUS_NAMES[coreStatus] || coreStatus;
        const availabilityName = ULD_AVAILABILITY_NAMES[availability] || availability;
        
        return {
            coreStatusName,
            availabilityName,
            coreStatus,
            availability
        };
    };

    return (
        <Modal
            title={
                <Space>
                    <DeleteOutlined style={{ color: '#ff4d4f' }} />
                    批量删除ULD
                </Space>
            }
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            destroyOnHidden
            width={800}
            okText="确认删除"
            cancelText="取消"
            okButtonProps={{ danger: true }}
        >
            <Form
                form={form}
                layout="vertical"
                preserve={false}
            >
                {/* 危险提示 */}
                <Alert
                    message="危险操作警告"
                    description={
                        <div>
                            <p>您即将批量删除 <Text strong style={{ color: '#ff4d4f' }}>{totalUlds}</Text> 个ULD，此操作不可撤销！</p>
                            <p>请确认您有权限进行此操作，并仔细检查要删除的ULD列表。</p>
                        </div>
                    }
                    type="error"
                    icon={<ExclamationCircleOutlined />}
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                {/* ULD统计信息 */}
                <Card size="small" style={{ marginBottom: 16 }}>
                    <Title level={5}>删除统计</Title>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                            <Text strong>总数量：</Text>
                            <Text>{totalUlds} 个ULD</Text>
                        </div>
                        
                        <div>
                            <Text strong>按类型分布：</Text>
                            <Space wrap style={{ marginLeft: 8 }}>
                                {Object.entries(uldsByType).map(([type, count]) => (
                                    <Tag key={type} color="blue">
                                        {type}: {count}
                                    </Tag>
                                ))}
                            </Space>
                        </div>

                        <div>
                            <Text strong>按核心状态分布：</Text>
                            <Space wrap style={{ marginLeft: 8 }}>
                                {Object.entries(uldsByCoreStatus).map(([statusName, count]) => (
                                    <Tag key={statusName} color="geekblue">
                                        {statusName}: {count}
                                    </Tag>
                                ))}
                            </Space>
                        </div>

                        <div>
                            <Text strong>按可用性分布：</Text>
                            <Space wrap style={{ marginLeft: 8 }}>
                                {Object.entries(uldsByAvailability).map(([availabilityName, count]) => (
                                    <Tag key={availabilityName} color="purple">
                                        {availabilityName}: {count}
                                    </Tag>
                                ))}
                            </Space>
                        </div>
                    </Space>
                </Card>

                {/* 删除原因 */}
                <Form.Item
                    name="delete_reason"
                    label="删除原因"
                    rules={[
                        { required: true, message: "请输入删除原因" },
                        { min: 5, message: "删除原因至少需要5个字符" }
                    ]}
                >
                    <TextArea
                        placeholder="请详细说明删除这些ULD的原因，此信息将记录在操作历史中"
                        autoSize={{ minRows: 3, maxRows: 6 }}
                        showCount
                        maxLength={500}
                    />
                </Form.Item>

                <Divider />

                {/* ULD详细列表 */}
                <div>
                    <Title level={5}>待删除ULD列表</Title>
                    <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: 4, padding: 8 }}>
                        <List
                            size="small"
                            dataSource={selectedUlds}
                            renderItem={(uld) => {
                                const statusInfo = getUldStatusDisplay(uld);
                                return (
                                <List.Item>
                                    <Space>
                                        <Text code>{uld.master.uld_identifier}</Text>
                                        <Text type="secondary">|</Text>
                                        <Text>{uld.master.uld_type}</Text>
                                        <Text type="secondary">|</Text>
                                        <Text>{uld.master.owner}</Text>
                                        <Text type="secondary">|</Text>
                                        <Tag color="blue" size="small">
                                                {statusInfo.coreStatusName}
                                            </Tag>
                                            <Tag color="purple" size="small">
                                                {statusInfo.availabilityName}
                                        </Tag>
                                    </Space>
                                </List.Item>
                                );
                            }}
                        />
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default BatchDeleteModal; 